import classNames from 'classnames/bind';
import { ICONS, Loader, Table, TableViewConfig } from '@creditsights/styleguide-web';
import React, { useCallback, useMemo } from 'react';
import CheckboxCellRenderer from '../../../../components/formatters/CheckboxCellRenderer/CheckboxCellRenderer';
import { CheckboxChangeEvent } from '../../../../components/formatters/CheckboxCellRenderer/CheckboxCellRenderer.types';
import { useUpdateMyContentPreferences } from '../../../../hooks/myContentPreferences';
import { Analyst, Filter } from '../../../../models';
import { Preference } from '../../../../models/Preference';
import NoContent from '../../NoContent/NoContent';
import AlertModeRenderer from './CellRenderers/AlertModeRenderer/AlertModeRenderer';
import CategoryCellRenderer from './CellRenderers/CategoryCellRenderer/CategoryCellRenderer';
import PreferenceTypesRenderer from './CellRenderers/PreferenceTypesRenderer/PreferenceTypesRenderer';
import styles from './Preferences.module.scss';

const cx = classNames.bind(styles);

// using index as a key may cause issues when/if we have sorting in the table
// ideally Interest should have some kind of id, but currently it doesn't exist in the backend
// if index as a key causes issues we could use concatenated values of interest tags as a key which is also not ideal but should prevent bugs
const getKey = (_: Preference, index: number) => index;

interface PreferencesProps {
  allAnalysts: Analyst[] | undefined;
  filterTypeTags: Filter[] | undefined;
  isLoading: boolean;
  onAddPreferenceClick: () => void;
  preferences: Preference[] | undefined;
  preferencesToRemove: number[];
  setPreferencesToRemove: (item: number[]) => void;
}

const Preferences = ({
  allAnalysts,
  filterTypeTags,
  isLoading,
  preferences,
  onAddPreferenceClick,
  preferencesToRemove,
  setPreferencesToRemove,
}: PreferencesProps): React.ReactElement => {
  const updatePreferences = useUpdateMyContentPreferences();

  const tags = useMemo(() => {
    return [...(filterTypeTags || []), { type: 'analyst', options: allAnalysts }];
  }, [filterTypeTags, allAnalysts]);

  const updatePreference = useCallback(
    <K extends keyof Preference>(index: number, key: K, value: Preference[K]) => {
      const preferenceToUpdate = { ...preferences![index] };

      if (!preferenceToUpdate) {
        return;
      }

      const preferencesToUpdate = [...preferences!];
      preferenceToUpdate[key] = value;
      preferencesToUpdate[index] = preferenceToUpdate;

      updatePreferences.mutate(preferencesToUpdate);
    },
    [preferences, updatePreferences]
  );

  const onAlertModeChange = useCallback(
    (value, index) => {
      updatePreference(index, 'mode', value);
    },
    [updatePreference]
  );

  const onMyCSToggle = useCallback(
    ({ index, checked }: CheckboxChangeEvent) => {
      updatePreference(index, 'myCS', checked);
    },
    [updatePreference]
  );

  const onPreferenceSelected = ({ checked, item, index }: CheckboxChangeEvent) => {
    if (!preferences) {
      return;
    }
    let preferenceIndexToRemove: number[] = [];
    // if item is null, it is header checkbox. Otherwise, item has value
    if (!item) {
      preferenceIndexToRemove = [...preferenceIndexToRemove, ...Array.from(preferences.keys())];
    }
    if (checked) {
      preferenceIndexToRemove = item ? [...preferencesToRemove, index] : preferenceIndexToRemove;
    } else {
      preferenceIndexToRemove = item
        ? preferencesToRemove.filter((preferenceToRemove) => preferenceToRemove !== index)
        : [];
    }
    setPreferencesToRemove(preferenceIndexToRemove);
  };

  const checkboxCellProps = {
    onClick: (e) => onPreferenceSelected(e),
    getIsChecked: (item, index) => (!item ? !!preferencesToRemove.length : preferencesToRemove.includes(index)),
    getIsIndeterminate: (item) =>
      item
        ? false
        : Boolean(preferencesToRemove.length && preferencesToRemove.length < (preferences ? preferences.length : 0)),
  };

  const columnsConfig: TableViewConfig = useMemo(
    () => ({
      columns: [
        {
          dataKey: 'deleteCheckbox',
          width: '2.8rem',
          cellDataRenderer: CheckboxCellRenderer,
          headerCellRenderer: CheckboxCellRenderer,
          cellRendererProps: checkboxCellProps,
        },
        {
          dataKey: 'tags',
          cellDataRenderer: CategoryCellRenderer,
          headerName: 'Category',
          width: '9.6rem',
          uniqueDataKey: 'category',
        },
        {
          dataKey: 'tags',
          cellDataRenderer: PreferenceTypesRenderer,
          cellRendererProps: { tags },
          headerName: 'Preference',
          uniqueDataKey: 'preference',
        },
        {
          dataKey: 'mode',
          cellDataRenderer: AlertModeRenderer,
          cellRendererProps: { onChange: onAlertModeChange },
          headerName: 'Alerts',
          width: '21.2rem',
        },
        {
          dataKey: 'myCS',
          cellDataRenderer: CheckboxCellRenderer,
          cellRendererProps: { onChange: onMyCSToggle, isToggle: true },
          headerName: 'My Feed',
          width: '9.6rem',
        },
        // Note, my-content/preferences "Edit" column/button was implemented, but removed with CSNP-2057.
        // Unit tests in ManagePreferencesTab.test were removed as well
        // Refer to this commit history in case it will ever be needed.
      ],
    }),
    [onAlertModeChange, onMyCSToggle, tags]
  );

  const renderContent = () => {
    return !!preferences?.length && filterTypeTags && allAnalysts ? (
      <Table data={preferences} tableViewConfig={columnsConfig} getKey={getKey} />
    ) : (
      <NoContent
        dataTestId="no-watchlist-saved"
        header="No Preferences yet"
        infoMessage="Add preferences to tailor the content on your feed."
        iconName={ICONS.NoContent}
        buttonParams={{
          iconName: ICONS.Plus,
          ariaLabel: 'Add Preference',
          title: 'Add Preference',
          onClick: onAddPreferenceClick,
        }}
      />
    );
  };

  return (
    <div className={cx('body')} data-testid="manage-preferences-tab">
      {isLoading ? <Loader position="center" /> : renderContent()}
    </div>
  );
};

export default Preferences;
